import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Mail, RefreshCw, CheckCircle, AlertCircle } from "lucide-react";
import { useB2BTranslation } from "../../B2BShared/hooks/useB2BTranslation";
import { useB2BAuth } from "../../B2BShared/B2BAuthContext/B2BAuthContext";
import "./B2BEmailVerification.css";

const B2BEmailVerification = () => {
  const { tAuth, i18n } = useB2BTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    resendVerification,
    isEmailVerified,
    user,
    isAuthenticated,
    isApproved,
    refreshUserData,
  } = useB2BAuth();

  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);

  // Refs to prevent race conditions and memory leaks
  const isComponentMounted = useRef(true);
  const statusCheckInterval = useRef(null);
  const countdownTimer = useRef(null);
  const redirectTimer = useRef(null);

  // Get email from location state or try to get from user context/localStorage as fallback
  const email = location.state?.email || user?.email || (() => {
    try {
      const storedUser = localStorage.getItem("b2bUser");
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        return parsed.originalEmail || parsed.email;
      }
    } catch (e) {
      // Error parsing stored user - continue with empty email
    }
    return null;
  })();

  // Cleanup function
  const cleanup = useCallback(() => {
    isComponentMounted.current = false;
    
    if (statusCheckInterval.current) {
      clearInterval(statusCheckInterval.current);
      statusCheckInterval.current = null;
    }
    
    if (countdownTimer.current) {
      clearTimeout(countdownTimer.current);
      countdownTimer.current = null;
    }
    
    if (redirectTimer.current) {
      clearTimeout(redirectTimer.current);
      redirectTimer.current = null;
    }
  }, []);

  // Safe state setters
  const safeSetState = useCallback((setter: () => void) => {
    if (isComponentMounted.current) {
      setter();
    }
  }, []);

  // Navigation with proper checks
  const safeNavigate = useCallback((path: string, delay: number = 0) => {
    if (!isComponentMounted.current) return;
    
    const navigateAction = () => {
      if (isComponentMounted.current) {
        navigate(path);
      }
    };

    if (delay > 0) {
      redirectTimer.current = setTimeout(navigateAction, delay);
    } else {
      navigateAction();
    }
  }, [navigate]);

  // Check user status and handle redirects
  const checkUserStatus = useCallback(async () => {
    // First check localStorage as fallback
    const token = localStorage.getItem("b2bToken");
    const userData = localStorage.getItem("b2bUser");

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);

        // IMPORTANT: Always check email verification first - don't redirect if email is not verified
        if (!parsedUser.isEmailVerified) {
          return false; // Stay on this page to show verification instructions
        }

        // After email verification, check profile completion
        const profileComplete = parsedUser.profileComplete || false;
        if (parsedUser.isEmailVerified && !profileComplete) {
          safeNavigate("/gcb/profile/create");
          return true;
        }

        // Only check approval if profile is complete
        if (parsedUser.isEmailVerified && profileComplete && !parsedUser.isApproved) {
          safeNavigate("/gcb/waiting-approval");
          return true;
        }

        if (parsedUser.isEmailVerified && profileComplete && parsedUser.isApproved) {
          safeNavigate("/gcb/dashboard");
          return true;
        }
      } catch (error) {
        // Error parsing user data - stay on page
      }
    }

    // Then check context state
    if (isAuthenticated && user) {
      // IMPORTANT: Always check email verification first - don't redirect if email is not verified
      if (!isEmailVerified()) {
        return false; // Stay on this page to show verification instructions
      }

      // After email verification, check profile completion
      // Profile must be completed before approval check
      const profileComplete = user?.profileComplete || false;
      if (isEmailVerified() && !profileComplete) {
        safeNavigate("/gcb/profile/create");
        return true;
      }

      // Only check approval if profile is complete
      if (isEmailVerified() && profileComplete && !isApproved()) {
        safeNavigate("/gcb/waiting-approval");
        return true;
      }

      if (isEmailVerified() && profileComplete && isApproved()) {
        safeNavigate("/gcb/dashboard");
        return true;
      }
    }

    return false;
  }, [isAuthenticated, user, isEmailVerified, isApproved, safeNavigate]);

  // Handle resend verification
  const handleResendVerification = useCallback(async () => {
    if (countdown > 0 || !email || isResending) return;

    safeSetState(() => {
      setIsResending(true);
      setResendError("");
      setResendSuccess(false);
    });

    try {
      await resendVerification(email);
      safeSetState(() => {
        setResendSuccess(true);
        setCountdown(60);
      });
    } catch (error) {
      if (error.response?.status === 400) {
        safeSetState(() => {
          setResendError("Your email is already verified. You should be redirected automatically.");
        });
        
        // Refresh and check status
        try {
          await refreshUserData();
          const shouldRedirect = await checkUserStatus();
          if (!shouldRedirect) {
            // Force redirect after a short delay if status check didn't trigger it
            safeNavigate("/gcb/waiting-approval", 1000);
          }
        } catch (refreshError) {
          // Error refreshing user data - continue with current state
        }
      } else {
        // Show the actual error message from the backend
        const errorMessage = error.message || error.response?.data?.message || "Failed to resend verification email. Please try again.";
        safeSetState(() => {
          setResendError(errorMessage);
        });
      }
    } finally {
      safeSetState(() => setIsResending(false));
    }
  }, [email, countdown, isResending, resendVerification, refreshUserData, checkUserStatus, safeNavigate, safeSetState]);

  // Component mount effect
  useEffect(() => {
    isComponentMounted.current = true;
    return cleanup;
  }, [cleanup]);

  // Language change effect
  useEffect(() => {
    const handleLanguageChange = () => {
      // Language changed - component will re-render automatically
    };

    i18n.on("languageChanged", handleLanguageChange);
    return () => i18n.off("languageChanged", handleLanguageChange);
  }, [i18n]);

  // Redirect if no email - but only after checking all possible sources
  useEffect(() => {
    // Give it a moment to load user data from context/localStorage
    const checkEmail = setTimeout(() => {
      if (!email) {
        safeNavigate("/gcb/register");
      }
    }, 500); // Small delay to allow context/localStorage to load

    return () => clearTimeout(checkEmail);
  }, [email, safeNavigate, user]);

  // Initial status check - but only redirect if email is verified
  useEffect(() => {
    const performInitialCheck = async () => {
      // Only check status if we have an email, otherwise wait
      if (!email) {
        // Wait a bit for email to load from context/localStorage
        setTimeout(() => {
          safeSetState(() => setIsCheckingStatus(false));
        }, 300);
        return;
      }

      const shouldRedirect = await checkUserStatus();
      if (!shouldRedirect) {
        safeSetState(() => setIsCheckingStatus(false));
      }
    };

    performInitialCheck();
  }, [checkUserStatus, safeSetState, email]);

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0) {
      countdownTimer.current = setTimeout(() => {
        safeSetState(() => setCountdown(prev => prev - 1));
      }, 1000);
    }

    return () => {
      if (countdownTimer.current) {
        clearTimeout(countdownTimer.current);
        countdownTimer.current = null;
      }
    };
  }, [countdown, safeSetState]);

  // Periodic status check effect
  useEffect(() => {
    if (!isCheckingStatus && isAuthenticated && user && !isEmailVerified()) {
      statusCheckInterval.current = setInterval(async () => {
        try {
          await refreshUserData();
          await checkUserStatus();
        } catch (error) {
          // Error during periodic status check - continue silently
        }
      }, 30000);
    }

    return () => {
      if (statusCheckInterval.current) {
        clearInterval(statusCheckInterval.current);
        statusCheckInterval.current = null;
      }
    };
  }, [isCheckingStatus, isAuthenticated, user, isEmailVerified, refreshUserData, checkUserStatus]);

  // Loading state - but show content if we have email, even if checking status
  // This ensures users see the verification message immediately after registration
  if (isCheckingStatus && !email) {
    return (
      <div className="email-verification-container">
        <div className="email-verification-content">
          <div className="email-verification-header">
            <RefreshCw className="email-icon spinning" size={64} />
            <h1>Checking your status...</h1>
            <p className="email-subtitle">
              Please wait while we verify your account status
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="email-verification-container">
      <div className="email-verification-content">
        {/* Prominent Notification Alert */}
        <div className="verification-alert">
          <AlertCircle className="alert-icon" size={24} />
          <div className="alert-content">
            <h2 className="alert-title">{tAuth("verificationRequired")}</h2>
            <p className="alert-message">{tAuth("verificationRequiredMessage")}</p>
            <p className="alert-note">{tAuth("afterVerification")}</p>
          </div>
        </div>

        <div className="email-verification-header">
          <Mail className="email-icon" size={64} />
          <h1>{tAuth("checkYourEmail")}</h1>
          <p className="email-subtitle">
            {tAuth("emailVerificationSubtitle")} <strong>{email}</strong>
          </p>
        </div>


        <div className="email-verification-info">
          <div className="info-box">
            <h3>{tAuth("didntReceiveEmail")}</h3>
            <ul>
              <li>{tAuth("checkSpamFolder")}</li>
              <li>{tAuth("makeSureEmailCorrect")} {email}</li>
              <li>{tAuth("waitForDelivery")}</li>
            </ul>
          </div>
        </div>

        <div className="resend-section">
          {resendSuccess && (
            <div className="success-message">
              <CheckCircle size={16} />
              {tAuth("verificationEmailSent")}
            </div>
          )}

          {resendError && (
            <div className="error-message">{resendError}</div>
          )}

          <button
            onClick={handleResendVerification}
            disabled={isResending || countdown > 0}
            className="resend-button"
          >
            {isResending ? (
              <>
                <RefreshCw className="spinning" size={16} />
                {tAuth("sending")}
              </>
            ) : countdown > 0 ? (
              `${tAuth("resendIn")} ${countdown}s`
            ) : (
              <>
                <RefreshCw size={16} />
                {tAuth("resendVerificationEmail")}
              </>
            )}
          </button>
        </div>

        <div className="email-verification-footer">
          <p>
            {tAuth("needHelp")}{" "}
            <a href="mailto:info@globalchic.club">
              {tAuth("contactSupport")}
            </a>
          </p>
          <a href="/gcb/register" className="back-link">
            {tAuth("backToRegistration")}
          </a>
        </div>
      </div>
    </div>
  );
};

export default B2BEmailVerification;